import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Loading from 'components/common/loading/Loading';
import { useGlobalRatingsQuery } from 'hooks/query/useSkillQueries';

import { getSearchSkillTree } from 'utils/skillV2';
import { handleError } from 'utils/errorHandler';

import * as skillService from 'services/skillService';

import { SkillSet } from './components';
import GlobalRatingModal from './skillV2Component/GlobalRatingModal';
import { SearchSkill } from 'components/common/skills';

import type { SkillNode } from 'interface/skillTree';
import type { SkillsV2Props } from './types';
import type { SkillOption } from 'components/common/skills/SearchSkill';

const Skills: React.FC<SkillsV2Props> = (props) => {
  const { openGlobalRatingModal, onGlobalRatingModalClose, isCategoryModalOpen, setIsCategoryModalOpen } = props;
  const {
    data: globalRatings = [],
    isLoading: isLoadingGlobalRating,
    isError: isGlobalRatingsError,
    error: globalRatingsError,
    errorUpdatedAt: globalRatingsErrorUpdatedAt,
  } = useGlobalRatingsQuery();

  const [skills, setSkills] = useState<SkillNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSearchSkill, setSelectedSearchSkill] = useState<SkillOption | null>(null);
  const [shouldFetchSkills, setShouldFetchSkills] = useState(false);
  const [triggerSearchSkillTree, setTriggerSearchSkillTree] = useState(0);
  const lastHandledGlobalRatingsErrorAtRef = React.useRef(0);

  const refetchSkills = useCallback(async () => {
    try {
      const { data } = (await skillService.fetchAll()) as { data: SkillNode[] };

      setSkills(data);
    } catch (error) {
      handleError(error);
    } finally {
      setTriggerSearchSkillTree((value) => value + 1);
    }
  }, []);

  useEffect(() => {
    const loadSkills = async () => {
      try {
        setIsLoading(true);

        const { data } = (await skillService.fetchAll()) as { data: SkillNode[] };

        setSkills(data);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
        setShouldFetchSkills(false);
      }
    };

    void loadSkills();
  }, [shouldFetchSkills]);

  useEffect(() => {
    if (
      !isGlobalRatingsError ||
      !globalRatingsError ||
      globalRatingsErrorUpdatedAt <= lastHandledGlobalRatingsErrorAtRef.current
    ) {
      return;
    }

    handleError(globalRatingsError);
    lastHandledGlobalRatingsErrorAtRef.current = globalRatingsErrorUpdatedAt;
  }, [isGlobalRatingsError, globalRatingsError, globalRatingsErrorUpdatedAt]);

  const searchSkillTree = useMemo(
    () => getSearchSkillTree(selectedSearchSkill ? { id: selectedSearchSkill.value } : null, skills),
    [skills, selectedSearchSkill]
  );

  const clearSelectedSearchSkill = useCallback(() => {
    setSelectedSearchSkill(null);
  }, []);

  return (
    <>
      <div className="full-scope-card">
        <div className="full-scope-card__header table-header name-wrap">
          <div>
            <div className="table-title">Skills</div>
          </div>
          <SearchSkill
            setSelectedSearchSkill={setSelectedSearchSkill}
            selectedSearchSkill={selectedSearchSkill}
            triggerSearchSkillTree={triggerSearchSkillTree}
          />
        </div>
        <div className={classNames({ loading__container: isLoading })}>
          <div className="full-scope-card__content d-flex position-relative primary-skills-card">
            {isLoading ? (
              <Loading className="mt-120" />
            ) : (
              <SkillSet
                skills={skills}
                globalRatings={globalRatings}
                setSelectedSearchSkill={clearSelectedSearchSkill}
                searchSkillTree={searchSkillTree}
                refechSkills={refetchSkills}
                isCategoryModalOpen={isCategoryModalOpen}
                setIsCategoryModalOpen={setIsCategoryModalOpen}
              />
            )}
          </div>
        </div>
      </div>

      <GlobalRatingModal
        globalRatings={globalRatings}
        isLoadingGlobalRating={isLoadingGlobalRating}
        openGlobalRatingModal={openGlobalRatingModal}
        onGlobalRatingModalClose={onGlobalRatingModalClose}
      />
    </>
  );
};

export default Skills;
